import { ArrowLeft, Zap } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { ProFeatureLock } from '@/features/billing/ui/pro-feature-lock';
import { useCreateRule, useAutomationRules } from '@/features/automation';
import { testIds } from '@/shared/testing';
import { useCafe24Coupons } from '@/features/coupon';
import { Button } from '@/shared/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/shared/ui/card';
import { AnalysisGuide } from '@/shared/ui/analysis-guide';
import { InfoTooltip } from '@/shared/ui/info-tooltip';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { AnimatedContainer } from '@/shared/ui/animated';
import { AppLayout } from '../-components/app-layout';

import type { AutomationTriggerType } from '@cafe24-apps/traffic-light-server/contracts';

const automationNewSearchSchema = z.object({
  triggerType: z.enum(['segment_change', 'ltv_cohort_change', 'overdue_entry']).optional(),
  fromSegments: z.string().optional(),
  toSegments: z.string().optional(),
  fromCohorts: z.string().optional(),
  toCohorts: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/$mallId/automation/new')({
  validateSearch: automationNewSearchSchema,
  component: NewAutomationRulePage,
});

const TRIGGER_DESCRIPTIONS: Record<AutomationTriggerType, string> = {
  segment_change: '고객 상태가 변경될 때 (예: 활성 → 주의)',
  ltv_cohort_change: 'LTV 등급이 변경될 때 (예: 성장 고객 → 이탈 위험)',
  overdue_entry: '평균 구매 주기를 넘긴 고객에게 자동으로 발급해요',
};

const SEGMENT_LABELS: Record<string, string> = {
  green: '활성',
  yellow: '주의',
  red: '이탈',
};

const COHORT_LABELS: Record<string, string> = {
  HIGH_VALUE: '우수 고객',
  GROWING: '성장 고객',
  AT_RISK: '이탈 위험',
  CHURNED: '이탈 고객',
};

const TRIGGER_GUIDES: Record<AutomationTriggerType, { title: string; tips: ReactNode[] }> = {
  segment_change: {
    title: '고객 상태 이동 트리거',
    tips: [
      <>
        고객의 상태가 <strong>이전 → 변경 후</strong>로 바뀔 때 쿠폰을 발급해요.
      </>,
      <>
        예를 들어 <strong>활성 → 주의</strong>로 떨어진 고객에게 리텐션 쿠폰을 보내면 이탈을 막을 수
        있어요.
      </>,
      <>
        반대로 <strong>이탈 → 활성</strong>으로 돌아온 고객에게 감사 쿠폰을 보내는 것도
        효과적이에요.
      </>,
    ],
  },
  ltv_cohort_change: {
    title: 'LTV 등급 이동 트리거',
    tips: [
      <>
        고객의 LTV 등급이 <strong>이전 → 변경 후</strong>로 바뀔 때 쿠폰을 발급해요.
      </>,
      <>
        <strong>성장 고객 → 이탈 위험</strong>으로 떨어진 고객은 이탈 직전이에요. 빠르게 쿠폰을
        보내면 재방문 확률이 높아져요.
      </>,
      <>LTV 등급은 누적 구매금액을 기반으로 산정돼요.</>,
    ],
  },
  overdue_entry: {
    title: '구매주기 초과 트리거',
    tips: [
      <>고객의 평균 구매 주기를 넘기면 자동으로 쿠폰을 발급해요.</>,
      <>재구매 주기가 30일인 고객이 35일째 안 사고 있다면, 리마인더 쿠폰이 효과적이에요.</>,
      <>
        별도의 이전/변경 후 설정 없이, <strong>구매주기 분석</strong> 결과를 바탕으로 대상을 자동
        감지해요.
      </>,
    ],
  },
};

const SEGMENT_OPTIONS = ['green', 'yellow', 'red'] as const;
const LTV_COHORT_OPTIONS = ['HIGH_VALUE', 'GROWING', 'AT_RISK', 'CHURNED'] as const;

function parseIntField(value: string, fallback: number, min: number, max: number): number {
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

function NewAutomationRulePage() {
  const { mallId } = useParams({ from: '/_authenticated/$mallId/automation/new' });
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { mutate: createRule, isPending } = useCreateRule();
  const { data: couponsData, isError: isCouponsError } = useCafe24Coupons();
  const { data: rulesData } = useAutomationRules({ limit: 100 });

  const [name, setName] = useState('');
  const [triggerType, setTriggerType] = useState<AutomationTriggerType>(
    search.triggerType ?? 'segment_change',
  );
  const [selectedCoupon, setSelectedCoupon] = useState('');
  const [excludeRecentDays, setExcludeRecentDays] = useState(30);
  const [cooldownDays, setCooldownDays] = useState(7);
  const [maxIssuesPerMember, setMaxIssuesPerMember] = useState(3);

  // Segment change config
  const [fromSegments, setFromSegments] = useState<string[]>(
    search.fromSegments?.split(',').filter(Boolean) ?? ['green'],
  );
  const [toSegments, setToSegments] = useState<string[]>(
    search.toSegments?.split(',').filter(Boolean) ?? ['yellow', 'red'],
  );

  // LTV cohort change config
  const [fromCohorts, setFromCohorts] = useState<string[]>(
    search.fromCohorts?.split(',').filter(Boolean) ?? ['GROWING'],
  );
  const [toCohorts, setToCohorts] = useState<string[]>(
    search.toCohorts?.split(',').filter(Boolean) ?? ['AT_RISK', 'CHURNED'],
  );

  const coupons = couponsData?.data ?? [];
  const existingRules = rulesData?.data ?? [];
  const isDuplicateName =
    name.trim().length > 0 && existingRules.some((r) => r.name === name.trim());

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error('규칙 이름을 입력하세요');
      return;
    }
    if (!selectedCoupon) {
      toast.error('쿠폰을 선택하세요');
      return;
    }

    if (
      triggerType === 'segment_change' &&
      (fromSegments.length === 0 || toSegments.length === 0)
    ) {
      toast.error('이전/변경 후 세그먼트를 각각 1개 이상 선택하세요');
      return;
    }
    if (
      triggerType === 'ltv_cohort_change' &&
      (fromCohorts.length === 0 || toCohorts.length === 0)
    ) {
      toast.error('이전/변경 후 코호트를 각각 1개 이상 선택하세요');
      return;
    }

    const coupon = coupons.find((c) => c.coupon_no === selectedCoupon);
    if (!coupon) return;

    let triggerConfig: Record<string, unknown> = {};
    if (triggerType === 'segment_change') {
      triggerConfig = { fromSegments, toSegments };
    } else if (triggerType === 'ltv_cohort_change') {
      triggerConfig = { fromCohorts, toCohorts };
    }

    createRule(
      {
        name: name.trim(),
        triggerType,
        triggerConfig,
        cafe24CouponNo: coupon.coupon_no,
        cafe24CouponName: coupon.coupon_name,
        excludeRecentDays,
        cooldownDays,
        maxIssuesPerMember,
      },
      {
        onSuccess: () => {
          navigate({ to: '/$mallId/automation', params: { mallId: mallId } });
        },
      },
    );
  };

  const toggleFromItem = (
    arr: string[],
    item: string,
    setter: (val: string[]) => void,
    oppositeArr: string[],
    oppositeSetter: (val: string[]) => void,
  ) => {
    const next = arr.includes(item) ? arr.filter((v) => v !== item) : [...arr, item];
    setter(next);
    oppositeSetter(oppositeArr.filter((v) => !next.includes(v)));
  };

  const toggleToItem = (arr: string[], item: string, setter: (val: string[]) => void) => {
    setter(arr.includes(item) ? arr.filter((v) => v !== item) : [...arr, item]);
  };

  return (
    <AppLayout pageTitle="자동화">
      <ProFeatureLock feature="couponAutoIssueEnabled">
        <div className="max-w-2xl mx-auto space-y-6">
          <AnimatedContainer delay={0}>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" asChild>
                <Link to="/$mallId/automation" params={{ mallId: mallId }}>
                  <ArrowLeft className="h-4 w-4" />
                  목록
                </Link>
              </Button>
            </div>
          </AnimatedContainer>

          <AnimatedContainer delay={0.1}>
            <Card>
              <CardHeader className="border-b">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Zap className="h-4 w-4 text-muted-foreground" />새 자동화 규칙
                </CardTitle>
                <CardDescription className="text-xs">
                  조건에 맞는 고객에게 쿠폰을 자동으로 발급해요. 규칙을 만들면 바로 활성화됩니다.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">규칙 이름</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="예: 이탈 위험 고객 자동 쿠폰"
                    className="text-sm"
                    maxLength={50}
                    data-testid={testIds.automation.nameInput}
                  />
                  {isDuplicateName && (
                    <p className="text-xs text-destructive">같은 이름의 규칙이 이미 있어요</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground inline-flex items-center">
                    트리거
                    <InfoTooltip content="어떤 상황에서 쿠폰을 발급할지 정하는 조건이에요. 고객 상태가 변하면 자동으로 작동해요." />
                  </Label>
                  <Select
                    value={triggerType}
                    onValueChange={(v) => setTriggerType(v as AutomationTriggerType)}
                  >
                    <SelectTrigger
                      className="text-sm"
                      data-testid={testIds.automation.triggerSelect}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="segment_change">고객 상태 이동</SelectItem>
                      <SelectItem value="ltv_cohort_change">LTV 등급 이동</SelectItem>
                      <SelectItem value="overdue_entry">구매주기 초과</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {TRIGGER_DESCRIPTIONS[triggerType]}
                  </p>
                </div>

                <AnalysisGuide title={TRIGGER_GUIDES[triggerType].title}>
                  <ul className="space-y-1 list-disc list-inside">
                    {TRIGGER_GUIDES[triggerType].tips.map((tip, i) => (
                      <li key={i}>{tip}</li>
                    ))}
                  </ul>
                </AnalysisGuide>

                {triggerType === 'segment_change' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">이전 상태</Label>
                      <div className="flex gap-2">
                        {SEGMENT_OPTIONS.map((seg) => (
                          <button
                            key={seg}
                            type="button"
                            data-testid={`${testIds.automation.fromSegmentButton}-${seg}`}
                            onClick={() =>
                              toggleFromItem(
                                fromSegments,
                                seg,
                                setFromSegments,
                                toSegments,
                                setToSegments,
                              )
                            }
                            className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
                              fromSegments.includes(seg)
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-card text-muted-foreground border-border hover:bg-accent'
                            }`}
                          >
                            {SEGMENT_LABELS[seg]}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">변경 후 상태</Label>
                      <div className="flex gap-2">
                        {SEGMENT_OPTIONS.map((seg) => {
                          const disabled = fromSegments.includes(seg);
                          return (
                            <button
                              key={seg}
                              type="button"
                              disabled={disabled}
                              data-testid={`${testIds.automation.toSegmentButton}-${seg}`}
                              onClick={() => toggleToItem(toSegments, seg, setToSegments)}
                              className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
                                disabled
                                  ? 'bg-muted text-muted-foreground/40 border-border cursor-not-allowed'
                                  : toSegments.includes(seg)
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : 'bg-card text-muted-foreground border-border hover:bg-accent'
                              }`}
                            >
                              {SEGMENT_LABELS[seg]}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {triggerType === 'ltv_cohort_change' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">이전 등급</Label>
                      <div className="flex flex-wrap gap-2">
                        {LTV_COHORT_OPTIONS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            data-testid={`${testIds.automation.fromCohortButton}-${c}`}
                            onClick={() =>
                              toggleFromItem(
                                fromCohorts,
                                c,
                                setFromCohorts,
                                toCohorts,
                                setToCohorts,
                              )
                            }
                            className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
                              fromCohorts.includes(c)
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-card text-muted-foreground border-border hover:bg-accent'
                            }`}
                          >
                            {COHORT_LABELS[c]}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">변경 후 등급</Label>
                      <div className="flex flex-wrap gap-2">
                        {LTV_COHORT_OPTIONS.map((c) => {
                          const disabled = fromCohorts.includes(c);
                          return (
                            <button
                              key={c}
                              type="button"
                              disabled={disabled}
                              data-testid={`${testIds.automation.toCohortButton}-${c}`}
                              onClick={() => toggleToItem(toCohorts, c, setToCohorts)}
                              className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
                                disabled
                                  ? 'bg-muted text-muted-foreground/40 border-border cursor-not-allowed'
                                  : toCohorts.includes(c)
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : 'bg-card text-muted-foreground border-border hover:bg-accent'
                              }`}
                            >
                              {COHORT_LABELS[c]}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">발급 쿠폰</Label>
                  {isCouponsError ? (
                    <p className="text-sm text-destructive">
                      쿠폰 목록을 불러오지 못했어요. 페이지를 새로고침해 주세요.
                    </p>
                  ) : (
                    <Select value={selectedCoupon} onValueChange={setSelectedCoupon}>
                      <SelectTrigger
                        className="text-sm"
                        data-testid={testIds.automation.couponSelect}
                      >
                        <SelectValue placeholder="Cafe24 쿠폰을 선택하세요" />
                      </SelectTrigger>
                      <SelectContent>
                        {coupons.map((c) => (
                          <SelectItem key={c.coupon_no} value={c.coupon_no}>
                            {c.coupon_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {!isCouponsError && (
                    <p className="text-xs text-muted-foreground">
                      Cafe24 쇼핑몰에 등록된 쿠폰 목록에서 선택해요
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground inline-flex items-center">
                      최근 발급 제외 (일)
                      <InfoTooltip content="이 기간 안에 같은 쿠폰을 이미 받은 고객은 자동 발급 대상에서 빠져요" />
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      max={365}
                      step={1}
                      value={excludeRecentDays}
                      onChange={(e) =>
                        setExcludeRecentDays(parseIntField(e.target.value, 0, 0, 365))
                      }
                      className="text-sm"
                      data-testid={testIds.automation.excludeRecentDaysInput}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground inline-flex items-center">
                      쿨다운 (일)
                      <InfoTooltip content="같은 고객에게 다시 발급하기까지 기다리는 기간이에요. 쿠폰 피로도를 줄여줘요." />
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      max={365}
                      step={1}
                      value={cooldownDays}
                      onChange={(e) => setCooldownDays(parseIntField(e.target.value, 0, 0, 365))}
                      className="text-sm"
                      data-testid={testIds.automation.cooldownDaysInput}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground inline-flex items-center">
                      회원당 최대 발급
                      <InfoTooltip content="한 고객이 이 규칙으로 받을 수 있는 쿠폰의 총 횟수예요" />
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      max={9999}
                      step={1}
                      value={maxIssuesPerMember}
                      onChange={(e) =>
                        setMaxIssuesPerMember(parseIntField(e.target.value, 1, 1, 9999))
                      }
                      className="text-sm"
                      data-testid={testIds.automation.maxIssuesInput}
                    />
                  </div>
                </div>
              </CardContent>
              <CardFooter className="border-t justify-end gap-2">
                <Button asChild variant="outline" data-testid={testIds.automation.cancelButton}>
                  <Link to="/$mallId/automation" params={{ mallId: mallId }}>
                    취소
                  </Link>
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isPending}
                  className="gap-1.5"
                  data-testid={testIds.automation.submitButton}
                >
                  <Zap className="h-4 w-4" />
                  {isPending ? '생성 중...' : '규칙 생성'}
                </Button>
              </CardFooter>
            </Card>
          </AnimatedContainer>
        </div>
      </ProFeatureLock>
    </AppLayout>
  );
}
